import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react'
import { BannerState } from 'src/Utils/globalTypes'
import {
  useAccountPurposeBanner,
  useAppStatusBanner,
  useAppVersionBanner,
  useDateLimitUpdateBanner,
  useUserRoleBanner,
  useImpersonateBanner,
} from 'src/global.hooks/banners.hooks'

export enum BannerType {
  APP_VERSION = 'APP_VERSION',
  USER_ROLE = 'USER_ROLE',
  DATE_LIMIT_UPDATE = 'DATE_LIMIT_UPDATE',
  ACCOUNT_PURPOSE = 'ACCOUNT_PURPOSE',
  APP_STATUS = 'APP_STATUS',
  IMPERSONATE_MODE = 'IMPERSONATE_MODE',
}

type BannerContextType = {
  banners: Record<BannerType, BannerState>
  bannerCount: number
  dismissBanner: (type: BannerType) => void
}

const initialBannerState: BannerState = {
  showBanner: false,
  message: '',
  isDismissed: false,
}

const initialBanners: Record<BannerType, BannerState> = {
  [BannerType.IMPERSONATE_MODE]: { ...initialBannerState },
  [BannerType.APP_VERSION]: { ...initialBannerState },
  [BannerType.USER_ROLE]: { ...initialBannerState },
  [BannerType.DATE_LIMIT_UPDATE]: { ...initialBannerState },
  [BannerType.ACCOUNT_PURPOSE]: { ...initialBannerState },
  [BannerType.APP_STATUS]: { ...initialBannerState },
}

export const BannerContext = createContext<BannerContextType>({
  banners: initialBanners,
  bannerCount: 0,
  dismissBanner: () => null,
})

export const BannerProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  const [banners, setBanners] =
    useState<Record<BannerType, BannerState>>(initialBanners)
  const [bannerCount, setBannerCount] = useState<number>(0)
  const dismissedBanners = useRef<Set<BannerType>>(new Set())

  // Impersonate Banner
  const impersonateBanner = useImpersonateBanner()

  // App Version Banner
  const appVersionBanner = useAppVersionBanner()

  // User Role Banner
  const userRoleBanner = useUserRoleBanner()

  // Account Purpose Banner
  const accountPurposeBanner = useAccountPurposeBanner()

  // Date Limit Update Banner
  const dateLimitUpdateBanner = useDateLimitUpdateBanner()

  // App Status Banner
  const appStatusBanner = useAppStatusBanner(
    dismissedBanners.current.has(BannerType.APP_STATUS),
  )

  // Memoize the banner update logic
  const updateBanners = useCallback(() => {
    const newBanners = { ...initialBanners }
    let count = 0

    const updateBanner = (type: BannerType, banner: BannerState) => {
      if (banner.showBanner && !dismissedBanners.current.has(type)) {
        newBanners[type] = banner
        count++
      }
    }

    updateBanner(BannerType.IMPERSONATE_MODE, impersonateBanner)
    updateBanner(BannerType.APP_VERSION, appVersionBanner)
    updateBanner(BannerType.APP_STATUS, appStatusBanner)
    updateBanner(BannerType.USER_ROLE, userRoleBanner)
    updateBanner(BannerType.ACCOUNT_PURPOSE, accountPurposeBanner)
    updateBanner(BannerType.DATE_LIMIT_UPDATE, dateLimitUpdateBanner)

    setBanners(newBanners)
    setBannerCount(count)
  }, [
    impersonateBanner,
    appVersionBanner,
    appStatusBanner,
    userRoleBanner,
    accountPurposeBanner,
    dateLimitUpdateBanner,
  ])

  useEffect(() => {
    updateBanners()
  }, [updateBanners])

  const dismissBanner = useCallback((type: BannerType) => {
    dismissedBanners.current.add(type)
    setBanners((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        isDismissed: true,
        showBanner: false,
      },
    }))
  }, [])

  return (
    <BannerContext.Provider
      value={{
        banners,
        bannerCount,
        dismissBanner,
      }}
    >
      {children}
    </BannerContext.Provider>
  )
}

export const useBannerContext = (): BannerContextType =>
  useContext(BannerContext)
